// 鶏 (inverted-jester) の「焦り狂い」案A (2026-11-03 作・11-04 から配布版の既定)。
//   11-04 作者の答え (遊び比べ 6Wj97wuQBA6SuHimoMiK58・db answers/rooster-frenzy-compare-1103): fall=hit1・feel=ok。
//     原文メモ「まだ僕がこのボスに対応しきれていないので細かいところを詰めたり後で変更はいるかも」。
//   開発版の比較: `?roosterFrenzy=off` = 前の仕組み (弾くほど加速・震え・src/rooster_rush.ts)・`&roosterFrenzyFall=reset` = 被弾で段0へ戻る版・
//     `&roosterFrenzyHud=1` = 頭の上に段の名を出す (配布版は出さない＝作者「言葉では伝えないが、試行回数で仕組みが理解できるような作り」)。
//   作者の答え (11-03・答えるページ XZT18tZg6uCom16RFERJrq): plan=A・rise=clean-string・fall=hit-1・peak=breath・vert=low-only。
//   メモ原文「3. 段が下がる条件：これはボスのHPや体幹の値を決めるのにも大きく影響があるので被弾したら段0 へ戻ると比べたい」
//     → 遊び比べて hit1 (被弾した連の終わりに1段下がる) を既定にした。
//   設計 = docs/research/2026-11-03-rooster-frenzy-design.md・作り = docs/research/2026-11-03-rooster-frenzy-build.md
//
// ここは読み込み順が回らないよう、何も読まない小さな箱 (段の表と切り替えだけ)。動き = src/rooster_frenzy.ts。
import { TOKI_CUT_TO_HIT_MS, TOKI_LONG_CHARGE_MS, TOKI_LONG_CUT_MS, TOKI_SHORT_CLUCK_MS } from './rooster_toki'

export type RoosterFrenzyMode = 'off' | 'a'
export type RoosterFrenzyFall = 'hit1' | 'reset'

let modeOverride: RoosterFrenzyMode | null = null
let fallOverride: RoosterFrenzyFall | null = null
let hudOverride: boolean | null = null
// 配布版の既定はこの初期値 (DEV の囲いの外で決める＝tests/dev_gated_defaults.test.ts)。
let urlMode: RoosterFrenzyMode = 'a'
let urlFall: RoosterFrenzyFall = 'hit1'
let urlHud = false
if (import.meta.env.DEV && typeof window !== 'undefined') {
  const q = new URLSearchParams(window.location.search)
  if (q.get('roosterFrenzy') === 'off') urlMode = 'off'
  if (q.get('roosterFrenzyFall') === 'reset') urlFall = 'reset'
  if (q.get('roosterFrenzyHud') === '1') urlHud = true
}
/** テスト用。null で URL の値へ戻す。 */
export function setRoosterFrenzyMode(m: RoosterFrenzyMode | null): void { modeOverride = m }
export function roosterFrenzyMode(): RoosterFrenzyMode { return modeOverride ?? urlMode }
/** テスト用。null で URL の値へ戻す。 */
export function setRoosterFrenzyFall(f: RoosterFrenzyFall | null): void { fallOverride = f }
export function roosterFrenzyFall(): RoosterFrenzyFall { return fallOverride ?? urlFall }
export function setRoosterFrenzyHud(v: boolean | null): void { hudOverride = v }
/** 段の名を頭の上に出すか (開発版 `&roosterFrenzyHud=1` だけ)。 */
export function roosterFrenzyHud(): boolean { return hudOverride ?? urlHud }

export const ROOSTER_FRENZY_BOSS_ID = 'inverted-jester'
export function roosterFrenzyOn(boss: { id: string }): boolean {
  return boss.id === ROOSTER_FRENZY_BOSS_ID && roosterFrenzyMode() === 'a'
}

export type FrenzyStage = 0 | 1 | 2 | 3

/** 段0・段1 の鬨ずらし (拍を変えた別の技)。段2・段3 は配布版の鬨ずらし (0.45 秒の拍) をつなぐ。 */
export interface FrenzyVariant {
  id: string
  /** 元にする配布版の技。 */
  base: 'tokiZurashiShort' | 'tokiZurashiLong'
  hits: number
  /** 爪の間 (当たりの終わり → 次の当たり)。間隔 = 当たり 130 + 間。床 320 を割らない。 */
  gapsMs: number[]
  /** 短い鳴きの「コッ」の始まり (溜めの頭から)。長い鳴きは空。 */
  clucksAtMs: number[]
  /** 鳴き止み (溜めの頭から)。 */
  cutMs: number
  chargeMs: number
  recoveryMs: number
}
const HIT_MS = 130
function shortVariant(id: string, hits: number, pitchMs: number, recoveryMs: number): FrenzyVariant {
  const clucksAtMs = Array.from({ length: hits }, (_, i) => i * pitchMs)
  const cutMs = clucksAtMs[hits - 1]! + TOKI_SHORT_CLUCK_MS
  return { id, base: 'tokiZurashiShort', hits, gapsMs: Array(hits - 1).fill(pitchMs - HIT_MS), clucksAtMs, cutMs, chargeMs: cutMs + TOKI_CUT_TO_HIT_MS, recoveryMs }
}
function longVariant(id: string, intervalsMs: number[], recoveryMs: number): FrenzyVariant {
  return { id, base: 'tokiZurashiLong', hits: intervalsMs.length + 1, gapsMs: intervalsMs.map(v => v - HIT_MS), clucksAtMs: [],
    cutMs: TOKI_LONG_CUT_MS, chargeMs: TOKI_LONG_CHARGE_MS, recoveryMs }
}

/**
 * 段の表 (設計の表のまま・docs/research/2026-11-03-rooster-frenzy-design.md)。
 *   拍 = 爪と爪の間隔 (ms)。短い鳴きは声の間も同じ間隔。長い鳴きは「タ……タタ」の詰めた所の間隔。
 *   chain = 鳴き直しをつなぐ数 (段2 = 2・段3 = 3。0 = つながない)。vertical = 縦の技 (暁呼び・宙廻り・鳴き挟み) を出すか。
 *   idleRate = 待機 (次の技を選ぶまで) の速さ。
 */
export const FRENZY_STAGES = [
  { stage: 0, name: '平静', pitchMs: 600, short: shortVariant('tokiFrenzy0Short', 3, 600, 680), long: longVariant('tokiFrenzy0Long', [1190, 600], 680), chain: 0, vertical: true, idleRate: 1 },
  { stage: 1, name: '苛立ち', pitchMs: 520, short: shortVariant('tokiFrenzy1Short', 4, 520, 560), long: longVariant('tokiFrenzy1Long', [1030, 520, 520], 560), chain: 0, vertical: true, idleRate: 2 },
  { stage: 2, name: '焦り', pitchMs: 450, short: null, long: null, chain: 2, vertical: false, idleRate: 3 },
  { stage: 3, name: '狂い', pitchMs: 450, short: null, long: null, chain: 3, vertical: false, idleRate: 3 },
] as const

export const FRENZY_VARIANTS: readonly FrenzyVariant[] = [FRENZY_STAGES[0].short, FRENZY_STAGES[0].long, FRENZY_STAGES[1].short, FRENZY_STAGES[1].long]
export function frenzyVariant(id: string): FrenzyVariant | undefined { return FRENZY_VARIANTS.find(v => v.id === id) }

export const ROOSTER_FRENZY = {
  /** 段2・段3 の鳴き直しの間 (前の爪の終わり → 次の鳴きの頭・ms)。休みなし。 */
  chainGapMs: 120,
  /** 段2 の最後の戻り (ms)。 */
  stage2RecoveryMs: 450,
  /** 段3 の最後の戻り (弾き切れなかった時・ms)。 */
  stage3RecoveryMs: 450,
  /** 段3 を弾き切った後の息切れ (ms)。この間は殴れる。明けると段0。 */
  breathMs: 2000,
} as const
